import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { protectSheetWithRandomPassword } from "../ExcelUtils";

const COLS_PER_BRANCH = 13;

export const loanSummaryExcel = async (
    data,
    headerStyle,
    subtotalStyle,
    title = "Loan Summary Report",
    loginPlaceId
) => {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet("Loan Summary");
    const amountFormat = "#,##0.00";
    const percentFormat = "0.00%";
    const printDate = new Date().toLocaleDateString("en-GB");

    // ---- Title Row ----
    const totalColumns =
        1 +
        data.reduce(
            (sum, district) =>
                sum +
                district.loanSummaryWithDivisionalDtoList.reduce(
                    (dsum, div) => dsum + div.loanSummaryWithBranchDtoList.length * COLS_PER_BRANCH,
                    0
                ),
            0
        );

    const titleRow = sheet.addRow([]);
    sheet.mergeCells(1, 2, 1, totalColumns);
    const titleCell = sheet.getCell(1, 2);
    titleCell.value = title;
    titleCell.font = { bold: true, size: 16 };
    titleRow.height = 25;

    // ---- Printed Date ----
    const dateRow = sheet.addRow([`Printed Date: ${printDate}`]);
    sheet.mergeCells(2, 1, 2, totalColumns);
    dateRow.alignment = { horizontal: "left" };
    dateRow.font = { italic: true, size: 11, color: { argb: "FF555555" } };

    // ---- District / Divisional / Branch Headers ----
    sheet.getRow(3);
    sheet.getRow(4);
    sheet.getRow(5);

    let colIndex = 2; // Column A is Product Name
    data.forEach((district) => {
        const districtStartCol = colIndex;
        let districtColSpan = 0;

        district.loanSummaryWithDivisionalDtoList.forEach((divisional) => {
            const divisionalStartCol = colIndex;
            const divisionalColSpan = divisional.loanSummaryWithBranchDtoList.length * COLS_PER_BRANCH;

            // Divisional Header
            sheet.mergeCells(4, divisionalStartCol, 4, divisionalStartCol + divisionalColSpan - 1);
            const divisionalCell = sheet.getRow(4).getCell(divisionalStartCol);
            divisionalCell.value = divisional.divisionalName;
            if (headerStyle) divisionalCell.style = headerStyle;
            divisionalCell.alignment = { horizontal: "center" };

            // Branch Headers
            divisional.loanSummaryWithBranchDtoList.forEach((branch) => {
                sheet.mergeCells(5, colIndex, 5, colIndex + COLS_PER_BRANCH - 1);
                const branchCell = sheet.getRow(5).getCell(colIndex);
                branchCell.value = branch.branchName;
                if (headerStyle) branchCell.style = headerStyle;
                branchCell.alignment = { horizontal: "center" };
                colIndex += COLS_PER_BRANCH;
            });

            districtColSpan += divisionalColSpan;
        });

        // District Header
        sheet.mergeCells(3, districtStartCol, 3, districtStartCol + districtColSpan - 1);
        const districtCell = sheet.getRow(3).getCell(districtStartCol);
        districtCell.value = district.districtName;
        if (headerStyle) districtCell.style = headerStyle;
        districtCell.alignment = { horizontal: "center" };
    });

    // ---- Column Names ----
    const columnNames = ["Product Name"];
    const allBranches = [];
    data.forEach((district) => {
        district.loanSummaryWithDivisionalDtoList.forEach((divisional) => {
            divisional.loanSummaryWithBranchDtoList.forEach((branch) => {
                allBranches.push(branch);
                columnNames.push(
                    "Disbursement Count",
                    "Disbursement Amount",
                    "Amount To Be Recovered",
                    "Amount Recovered",
                    "Recovered %",
                    "Outstanding Count",
                    "Outstanding Amount",
                    "Default Willful Count",
                    "Default Willful Amount",
                    "Defaulted %",
                    "Pass Due Count",
                    "Pass Due Amount",
                    "Pass Due %"
                );
            });
        });
    });
    const headerRow = sheet.addRow(columnNames);
    headerRow.eachCell((cell) => {
        if (headerStyle) cell.style = headerStyle;
        cell.alignment = { horizontal: "center" };
    });

    const firstDataRow = headerRow.number + 1; // row 7

    // ---- Data Rows ----
    const productNames = Array.from(
        new Set(allBranches.flatMap((branch) => branch.loanSummaryDtoList.map((p) => p.productName)))
    );

    productNames.forEach((productName, rowIndex) => {
        const rowValues = [productName];

        allBranches.forEach((branch) => {
            const p = branch.loanSummaryDtoList.find((x) => x.productName === productName);
            if (p) {
                rowValues.push(
                    p.disbursementCount,
                    p.disbursementAmount,
                    p.amountToBeRecovered,
                    p.amountRecovered,
                    null, // Recovered %
                    p.outStandingCount,
                    p.outStandingAmount,
                    p.defaultWilFulCount,
                    p.defaultWilFulAmount,
                    null, // Defaulted %
                    p.passDueCount,
                    p.passDueAmount,
                    null // Pass Due %
                );
            } else {
                rowValues.push(...Array(COLS_PER_BRANCH).fill(null));
            }
        });

        const dataRow = sheet.addRow(rowValues);
        const r = dataRow.number;

        // Formulas for Recovered %, Defaulted %, Pass Due %
        let colBase = 2;
        allBranches.forEach(() => {
            const L = (c) => sheet.getColumn(c).letter;
            const disbAmt = `${L(colBase + 1)}${r}`;
            const recoveredAmt = `${L(colBase + 3)}${r}`;
            const outAmt = `${L(colBase + 6)}${r}`;
            const defaultedAmt = `${L(colBase + 8)}${r}`;
            const passDueAmt = `${L(colBase + 11)}${r}`;

            const recoveredPctCell = dataRow.getCell(colBase + 4);
            recoveredPctCell.value = { formula: `IF(${disbAmt}>0,${recoveredAmt}/${disbAmt},"")` };
            recoveredPctCell.numFmt = percentFormat;

            const defaultedPctCell = dataRow.getCell(colBase + 9);
            defaultedPctCell.value = { formula: `IF(${outAmt}>0,${defaultedAmt}/${outAmt},"")` };
            defaultedPctCell.numFmt = percentFormat;

            const passDuePctCell = dataRow.getCell(colBase + 12);
            passDuePctCell.value = { formula: `IF(${outAmt}>0,${passDueAmt}/${outAmt},"")` };
            passDuePctCell.numFmt = percentFormat;

            colBase += COLS_PER_BRANCH;
        });

        // Zebra striping
        if (rowIndex % 2 === 1) {
            dataRow.eachCell((cell) => {
                cell.fill = {
                    type: "pattern",
                    pattern: "solid",
                    fgColor: { argb: "FFDCDCDC" },
                };
            });
        }

        // Amount formatting
        let amountColBase = 2;
        allBranches.forEach(() => {
            [1, 2, 3, 6, 8, 11].forEach((offset) => {
                const cell = dataRow.getCell(amountColBase + offset);
                if (cell.value !== null && cell.value !== undefined) cell.numFmt = amountFormat;
            });
            amountColBase += COLS_PER_BRANCH;
        });
    });

    // ---- Subtotal Row ----
    const subtotalRowValues = ["Total"];
    const startRow = firstDataRow;
    const endRow = sheet.lastRow.number;
    let colStart = 2;

    allBranches.forEach(() => {
        const colIndexes = Array.from({ length: COLS_PER_BRANCH }, (_, i) => colStart + i);
        const L = (c) => sheet.getColumn(c).letter;
        const rng = (c) => `${L(c)}${startRow}:${L(c)}${endRow}`;

        const disbAmtCol = colIndexes[1];
        const recoveredAmtCol = colIndexes[3];
        const recoveredPctCol = colIndexes[4];
        const outStandingAmtCol = colIndexes[6];
        const defaultedAmtCol = colIndexes[8];
        const defaultedPctCol = colIndexes[9];
        const passDueAmtCol = colIndexes[11];
        const passDuePctCol = colIndexes[12];

        colIndexes.forEach((col) => {
            if (col === recoveredPctCol) {
                subtotalRowValues[col - 1] = {
                    formula: `IF(SUM(${rng(disbAmtCol)})>0,SUM(${rng(recoveredAmtCol)})/SUM(${rng(disbAmtCol)}),0)`,
                };
            } else if (col === defaultedPctCol) {
                subtotalRowValues[col - 1] = {
                    formula: `IF(SUM(${rng(outStandingAmtCol)})>0,SUM(${rng(defaultedAmtCol)})/SUM(${rng(outStandingAmtCol)}),0)`,
                };
            } else if (col === passDuePctCol) {
                subtotalRowValues[col - 1] = {
                    formula: `IF(SUM(${rng(outStandingAmtCol)})>0,SUM(${rng(passDueAmtCol)})/SUM(${rng(outStandingAmtCol)}),0)`,
                };
            } else {
                subtotalRowValues[col - 1] = { formula: `SUM(${rng(col)})` };
            }
        });

        colStart += COLS_PER_BRANCH;
    });

    const subtotalRow = sheet.addRow(subtotalRowValues);
    subtotalRow.eachCell((cell) => {
        if (subtotalStyle) {
            cell.font = subtotalStyle.font;
            cell.fill = subtotalStyle.fill;
            cell.alignment = subtotalStyle.alignment;
            cell.border = subtotalStyle.border;
        }
    });

    // Format subtotal percentages and amounts
    allBranches.forEach((_, i) => {
        const baseCol = 2 + i * COLS_PER_BRANCH;
        [4, 9, 12].forEach((o) => (subtotalRow.getCell(baseCol + o).numFmt = percentFormat));
        [1, 2, 3, 6, 8, 11].forEach((o) => (subtotalRow.getCell(baseCol + o).numFmt = amountFormat));
    });

    // ---- Column Widths ----
    sheet.columns.forEach((col, i) => {
        col.width = i === 0 ? 60 : 20;
    });

    // ---- Thin border for all cells (full grid) ----
    const lastRowNum = sheet.lastRow.number;
    const lastCol = sheet.columnCount;

    for (let r = 1; r <= lastRowNum; r++) {
        const row = sheet.getRow(r);
        for (let c = 1; c <= lastCol; c++) {
            row.getCell(c).border = {
                top: { style: "thin" },
                left: { style: "thin" },
                bottom: { style: "thin" },
                right: { style: "thin" },
            };
        }
    }

    // ---- Vertical hierarchy borders ----
    let runningCol = 2;

    data.forEach((district) => {
        let districtEnd = runningCol;

        district.loanSummaryWithDivisionalDtoList.forEach((divisional) => {
            let divisionalEnd = runningCol;

            divisional.loanSummaryWithBranchDtoList.forEach(() => {
                const branchEnd = runningCol + COLS_PER_BRANCH - 1;

                // Branch border (yellow)
                for (let r = 1; r <= lastRowNum; r++) {
                    const cell = sheet.getRow(r).getCell(branchEnd);
                    cell.border = {
                        ...cell.border,
                        right: { style: "thin", color: { argb: "FFFFFF00" } },
                    };
                }

                runningCol += COLS_PER_BRANCH;
                divisionalEnd = runningCol - 1;
            });

            // Divisional border (blue)
            for (let r = 1; r <= lastRowNum; r++) {
                const cell = sheet.getRow(r).getCell(divisionalEnd);
                cell.border = {
                    ...cell.border,
                    right: { style: "medium", color: { argb: "FF0000FF" } },
                };
            }

            districtEnd = divisionalEnd;
        });

        // District border (red)
        for (let r = 1; r <= lastRowNum; r++) {
            const cell = sheet.getRow(r).getCell(districtEnd);
            cell.border = {
                ...cell.border,
                right: { style: "thick", color: { argb: "FFFF0000" } },
            };
        }
    });

    // ---- Freeze Header ----
    sheet.views = [{ state: "frozen", xSplit: 1, ySplit: 6 }];

    // =====================================================================
    // ---- Grand Total Sheet ----
    // =====================================================================
    const gtSheet = workbook.addWorksheet("Grand Total");
    const gtColumns = [
        "Product Name",
        "Disbursement Count",
        "Disbursement Amount",
        "Amount To Be Recovered",
        "Amount Recovered",
        "Recovered %",
        "Outstanding Count",
        "Outstanding Amount",
        "Default Willful Count",
        "Default Willful Amount",
        "Defaulted %",
        "Pass Due Count",
        "Pass Due Amount",
        "Pass Due %",
    ];
    const gtLastCol = gtColumns.length; // 14

    // Title + date
    gtSheet.addRow([`${title} - Grand Total`]);
    gtSheet.mergeCells(1, 1, 1, gtLastCol);
    gtSheet.getCell(1, 1).font = { bold: true, size: 16 };
    gtSheet.getRow(1).height = 25;

    gtSheet.addRow([`Printed Date: ${printDate}`]);
    gtSheet.mergeCells(2, 1, 2, gtLastCol);
    gtSheet.getCell(2, 1).font = { italic: true, size: 11, color: { argb: "FF555555" } };

    // Header
    const gtHeaderRow = gtSheet.addRow(gtColumns);
    gtHeaderRow.eachCell((cell) => {
        if (headerStyle) cell.style = headerStyle;
        cell.alignment = { horizontal: "center" };
    });

    // Aggregate per product across all branches
    const totalsByProduct = {};
    allBranches.forEach((branch) => {
        branch.loanSummaryDtoList.forEach((p) => {
            if (!totalsByProduct[p.productName]) {
                totalsByProduct[p.productName] = {
                    disbCount: 0, disbAmt: 0, toRecover: 0, recovered: 0,
                    osCount: 0, osAmt: 0, dwCount: 0, dwAmt: 0, pdCount: 0, pdAmt: 0,
                };
            }
            const t = totalsByProduct[p.productName];
            t.disbCount += Number(p.disbursementCount) || 0;
            t.disbAmt += Number(p.disbursementAmount) || 0;
            t.toRecover += Number(p.amountToBeRecovered) || 0;
            t.recovered += Number(p.amountRecovered) || 0;
            t.osCount += Number(p.outStandingCount) || 0;
            t.osAmt += Number(p.outStandingAmount) || 0;
            t.dwCount += Number(p.defaultWilFulCount) || 0;
            t.dwAmt += Number(p.defaultWilFulAmount) || 0;
            t.pdCount += Number(p.passDueCount) || 0;
            t.pdAmt += Number(p.passDueAmount) || 0;
        });
    });

    const gtStartRow = gtHeaderRow.number + 1; // row 4
    const pctFormula = (num, den, r) => ({
        formula: `IF(${den}${r}>0,${num}${r}/${den}${r},"")`,
    });

    productNames.forEach((name, i) => {
        const t = totalsByProduct[name];
        const row = gtSheet.addRow([
            name,
            t.disbCount, t.disbAmt, t.toRecover, t.recovered, null,
            t.osCount, t.osAmt,
            t.dwCount, t.dwAmt, null,
            t.pdCount, t.pdAmt, null,
        ]);
        const r = row.number;
        row.getCell(6).value = pctFormula("E", "C", r); // Recovered % = Recovered / Disbursed
        row.getCell(11).value = pctFormula("J", "H", r); // Defaulted % = Willful / Outstanding
        row.getCell(14).value = pctFormula("M", "H", r); // Pass Due % = Pass Due / Outstanding

        if (i % 2 === 1) {
            row.eachCell({ includeEmpty: true }, (cell) => {
                cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFDCDCDC" } };
            });
        }
    });

    // Grand total row
    const gtEndRow = gtSheet.lastRow.number;
    const sumCol = (L) => ({ formula: `SUM(${L}${gtStartRow}:${L}${gtEndRow})` });
    const gtTotalRow = gtSheet.addRow([
        "Grand Total",
        sumCol("B"), sumCol("C"), sumCol("D"), sumCol("E"), null,
        sumCol("G"), sumCol("H"),
        sumCol("I"), sumCol("J"), null,
        sumCol("L"), sumCol("M"), null,
    ]);
    const tr = gtTotalRow.number;
    gtTotalRow.getCell(6).value = pctFormula("E", "C", tr);
    gtTotalRow.getCell(11).value = pctFormula("J", "H", tr);
    gtTotalRow.getCell(14).value = pctFormula("M", "H", tr);

    gtTotalRow.eachCell({ includeEmpty: true }, (cell) => {
        if (subtotalStyle) {
            cell.font = subtotalStyle.font;
            cell.fill = subtotalStyle.fill;
            cell.alignment = subtotalStyle.alignment;
        }
    });

    // Number formats, borders, widths, freeze
    const gtAmountCols = [3, 4, 5, 8, 10, 13];
    const gtPctCols = [6, 11, 14];
    for (let r = gtStartRow; r <= tr; r++) {
        const row = gtSheet.getRow(r);
        gtAmountCols.forEach((c) => (row.getCell(c).numFmt = amountFormat));
        gtPctCols.forEach((c) => (row.getCell(c).numFmt = percentFormat));
    }
    for (let r = 1; r <= tr; r++) {
        for (let c = 1; c <= gtLastCol; c++) {
            gtSheet.getRow(r).getCell(c).border = {
                top: { style: "thin" },
                left: { style: "thin" },
                bottom: { style: "thin" },
                right: { style: "thin" },
            };
        }
    }
    gtSheet.columns.forEach((col, i) => {
        col.width = i === 0 ? 60 : 20;
    });
    gtSheet.views = [{ state: "frozen", xSplit: 1, ySplit: 3 }];

    // ---- Optional Protection ----
    if (loginPlaceId === "1") {
        protectSheetWithRandomPassword(sheet, 12);
        protectSheetWithRandomPassword(gtSheet, 12);
    }

    // ---- Save File ----
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, `Loan Summary-${new Date().toISOString().split("T")[0]}.xlsx`);
};